import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { Form, Field } from "react-final-form";
import { Autocomplete } from "mui-rff";
import styles from "./CreatePlaylistModal.module.scss";

type Option = {
  label: string;
  value: number | string;
  [key: string]: any;
};

interface CreatePlaylistModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => void | Promise<void>;
  treatmentPlans: Option[];
  onTreatmentChange: (event: any, newValue: Option[]) => void;
}

const validate = (values: any) => {
  const errors: Record<string, string> = {};

  if (!values.playlistName?.trim()) {
    errors.playlistName = "Playlist name is required";
  }

  if (!values.treatment_idsnew?.length) {
    errors.treatment_idsnew = "Select at least one treatment";
  }

  return errors;
};

const CreatePlaylistModal: React.FC<CreatePlaylistModalProps> = ({
  open,
  onClose,
  onSubmit,
  treatmentPlans,
  onTreatmentChange,
}) => {
  const handleFormSubmit = async (values: any) => {
    // Ensure category_id is never sent to the API.
    const { category_id, ...payload } = values;
    await onSubmit(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: styles.createPlaylistDialog }}
    >
      <DialogTitle className={styles.dialogTitle}>
        Add New Playlist
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Form
          onSubmit={handleFormSubmit}
          validate={validate}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} className={styles.dialogForm}>
              <Box className={styles.headerBlock}>
                <h3>Create playlist</h3>
                <p>
                  Add a playlist name and link the treatments that should appear
                  under this playlist.
                </p>
              </Box>

              <Box className={styles.dialogFieldStack}>
                <Field name="playlistName">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label="Playlist name"
                      placeholder="Enter playlist name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      autoFocus
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched ? meta.error : undefined}
                    />
                  )}
                </Field>

                <Autocomplete
                  name="treatment_idsnew"
                  label="Linked treatments"
                  multiple
                  required
                  options={treatmentPlans}
                  getOptionLabel={(option: Option) => option.label || ""}
                  isOptionEqualToValue={(option: Option, value: Option) =>
                    option.value === value.value
                  }
                  onChange={onTreatmentChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Linked treatments"
                      placeholder="Search and select treatments"
                      size="small"
                    />
                  )}
                />
              </Box>

              <DialogActions className={styles.dialogActions}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className={styles.cancelBtn}
                  disabled={submitting}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  className={styles.primaryBtn}
                  disabled={submitting}
                >
                  {submitting ? "Adding..." : "Add Playlist"}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CreatePlaylistModal;